import { lazy, Suspense, useEffect, useState } from 'react';
import type { CharacterKey } from '@secretario/shared';
import { characterTile, EXPRESSIONS, type Expression } from '../../lib/characters.js';
import type { LiveMood } from '../../lib/voice/controller.js';
import type { LiveDrive } from '../../lib/voice/liveDriver.js';
import { cardMood, isLayeredCharacter } from '../voice/characters3d.js';
import { LiveStage2D } from '../voice/LiveStage2D.js';

const LiveStageLayers = lazy(() =>
  import('../voice/LiveStageLayers.js').then((module) => ({ default: module.LiveStageLayers })),
);

const NO_SOUND = () => ({ input: 0, output: 0, voice: null });

function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * O personagem do palco, atrás de uma interface só: quem usa não sabe qual
 * motor está desenhando (camadas vivas, imagem com respiração e, mais adiante,
 * Rive). O enquadramento (cheio, busto, selo) é do palco, não daqui.
 */
export function CharacterStage({
  characterKey,
  expression,
  mood,
  say,
  paused = false,
  still = false,
  live = null,
  asleep = false,
  onReady,
  name,
}: {
  characterKey: CharacterKey;
  /** Traço dominante da personalidade escolhida. */
  expression: Expression;
  /** Reação do momento; sem ela vale o humor do traço dominante. */
  mood?: LiveMood;
  /** O que ele "diz" agora: a boca acompanha quando o texto muda. */
  say?: string;
  /** Fora de cena (uma página por cima): para de gastar quadros. */
  paused?: boolean;
  /** Modo silencioso: fica quieto, sem fala nem passeio de expressão. */
  still?: boolean;
  /** Conversa ao vivo de pé: o mesmo personagem passa a ouvir e falar de verdade. */
  live?: LiveDrive | null | undefined;
  /** Abertura do app: de olhos fechados até o perfil confirmar. */
  asleep?: boolean;
  /** O personagem está desenhado e vivo na tela (fecha a tela nativa de abertura). */
  onReady?: (() => void) | undefined;
  name: string;
}) {
  const layered = isLayeredCharacter(characterKey);
  const quiet = still || prefersReducedMotion();
  const talking = !!live;

  // Personagens em imagem: de vez em quando passeiam por outra expressão e voltam.
  const [visiting, setVisiting] = useState<Expression | null>(null);
  useEffect(() => {
    if (layered || quiet || paused || talking) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let back: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timer = setTimeout(
        () => {
          if (cancelled) return;
          const others = EXPRESSIONS.filter((item) => item !== expression);
          setVisiting(others[Math.floor(Math.random() * others.length)] ?? null);
          back = setTimeout(() => {
            if (!cancelled) setVisiting(null);
          }, 1900);
          schedule();
        },
        8000 + Math.random() * 7000,
      );
    };
    schedule();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearTimeout(back);
    };
  }, [layered, quiet, paused, expression, talking]);

  const stillImage = characterTile(characterKey, expression);
  if (layered) {
    return (
      <div className="stg-char">
        <Suspense fallback={<img className="stg-char-img" src={stillImage} alt="" />}>
          <LiveStageLayers
            characterKey={characterKey}
            phase={live ? live.phase : 'listening'}
            mood={live ? live.mood : (mood ?? cardMood(characterKey, expression))}
            levels={live ? live.levels : NO_SOUND}
            ambient={!live}
            layout="stage"
            paused={paused}
            asleep={asleep}
            onReady={onReady}
            {...(say && !quiet && !live ? { say } : {})}
          />
        </Suspense>
        <span className="stg-sr">{name}</span>
      </div>
    );
  }
  if (live) {
    return (
      <div className="stg-char stg-char-flat">
        <LiveStage2D
          characterKey={characterKey}
          phase={live.phase}
          mood={live.mood}
          levels={live.levels}
        />
        <span className="stg-sr">{name}</span>
      </div>
    );
  }
  return (
    <div className={`stg-char stg-char-flat${quiet ? ' quiet' : ''}`}>
      <img className="stg-char-img" src={stillImage} alt={name} />
      <img
        className={`stg-char-img stg-char-visit${visiting ? ' on' : ''}`}
        src={characterTile(characterKey, visiting ?? expression)}
        alt=""
        aria-hidden="true"
      />
    </div>
  );
}
